import { makeStyles, useTheme } from '@material-ui/core/styles';
import type { Theme } from '@material-ui/core/styles';

import type { SpacingDefinition } from './KlpSpacing';
import type { KlpStyled } from './KlpStyles';

export const useKlpStyles = <T extends Record<string, any>>(
    props: KlpStyled,
    extraStyles?: T | ((theme: Theme) => T),
): Record<keyof T | 'klpStyles', string> => {
    const theme = useTheme();

    // Resolve extraStyles (object or function)
    const resolvedStyles: T =
        typeof extraStyles === 'function'
            ? extraStyles(theme)
            : ((extraStyles || {}) as T);

    // Create dynamic styles with makeStyles
    const useDynamicStyles = makeStyles(() => ({
        ...resolvedStyles,
        klpStyles: {
            color: () => {
                const { klpColor, klpPalette } = props;

                if (typeof klpColor === 'string' && klpColor.startsWith('#')) {
                    return klpColor;
                }

                if (klpPalette && klpColor) {
                    const palette = theme.palette[klpPalette];
                    return palette?.[klpColor as keyof typeof palette];
                }

                return undefined;
            },
            fontSize: () => {
                if (props.klpHeadingLevel) return undefined;
                if (props.klpTypographyVariant) {
                    return theme.klpVariant[props.klpTypographyVariant]
                        .fontSize;
                }
                return undefined;
            },
            lineHeight: () => {
                if (props.klpHeadingLevel) return undefined;
                if (props.klpTypographyVariant) {
                    return theme.klpVariant[props.klpTypographyVariant]
                        .lineHeight;
                }
                return undefined;
            },
            justifyContent: () => props.justifyContent,

            [theme.breakpoints.down('sm')]: createStylesForBreakpoint(
                'mobile',
                theme,
                props,
            ),
            [theme.breakpoints.only('md')]: createStylesForBreakpoint(
                'tablet',
                theme,
                props,
            ),
            [theme.breakpoints.up('md')]: createStylesForBreakpoint(
                'desktop',
                theme,
                props,
            ),
        },
    }));

    // Generate CSS class names
    const dynamicClasses = useDynamicStyles();

    // Ensure return type includes all keys from `resolvedStyles` and `klpStyles`
    return dynamicClasses as Record<keyof T | 'klpStyles', string>;
};

const createStylesForBreakpoint = (
    mode: keyof SpacingDefinition,
    theme: Theme,
    props: KlpStyled,
) => {
    const {
        margin,
        marginHorizontal,
        marginVertical,
        marginLeft,
        marginRight,
        marginTop,
        marginBottom,
        padding,
        paddingHorizontal,
        paddingVertical,
        paddingLeft,
        paddingRight,
        paddingTop,
        paddingBottom,
    } = props;
    return {
        marginTop: () =>
            getThemeValue(mode, 'margin', theme, {
                marginTop,
                marginVertical,
                margin,
            }),
        marginBottom: () =>
            getThemeValue(mode, 'margin', theme, {
                marginBottom,
                marginVertical,
                margin,
            }),
        marginLeft: () =>
            getThemeValue(mode, 'margin', theme, {
                marginLeft,
                marginHorizontal,
                margin,
            }),
        marginRight: () =>
            getThemeValue(mode, 'margin', theme, {
                marginRight,
                marginHorizontal,
                margin,
            }),
        paddingTop: () =>
            getThemeValue(mode, 'padding', theme, {
                paddingTop,
                paddingVertical,
                padding,
            }),
        paddingBottom: () =>
            getThemeValue(mode, 'padding', theme, {
                paddingBottom,
                paddingVertical,
                padding,
            }),
        paddingLeft: () =>
            getThemeValue(mode, 'padding', theme, {
                paddingLeft,
                paddingHorizontal,
                padding,
            }),
        paddingRight: () =>
            getThemeValue(mode, 'padding', theme, {
                paddingRight,
                paddingHorizontal,
                padding,
            }),
    };
};

// Utility function to compute theme-based values
const getThemeValue = (
    mode: keyof SpacingDefinition,
    cssProp: 'margin' | 'padding',
    theme: Theme,
    props: KlpStyled,
): string | undefined => {
    const {
        margin,
        marginHorizontal,
        marginVertical,
        marginLeft,
        marginRight,
        marginTop,
        marginBottom,
        padding,
        paddingHorizontal,
        paddingVertical,
        paddingLeft,
        paddingRight,
        paddingTop,
        paddingBottom,
    } = props;

    switch (cssProp) {
        case 'margin': {
            const prop =
                marginLeft ||
                marginRight ||
                marginTop ||
                marginBottom ||
                marginHorizontal ||
                marginVertical ||
                margin;
            return prop && theme.klpSpacing[prop]
                ? theme.klpSpacing[prop][mode]
                : undefined;
        }
        case 'padding': {
            const prop =
                paddingLeft ||
                paddingRight ||
                paddingTop ||
                paddingBottom ||
                paddingHorizontal ||
                paddingVertical ||
                padding;

            return prop && theme.klpSpacing[prop]
                ? theme.klpSpacing[prop][mode]
                : undefined;
        }
        default:
            return undefined;
    }
};
